/**
 * The vault's transient notice — one hook for the state and one component for the
 * markup, mounted by both the routed shell (`DocumentsRoute`) and the legacy
 * single-route one (`DocumentVault`).
 *
 * Both used to hand-roll this: the same `useState`, the same bare `3200` timer, the
 * same markup down to the tick glyph and the same stylesheet. Two copies of a timing
 * value and an `aria-live` region is where they start drifting.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@jsq/ds/icons";
import * as styles from "./styles_vanilla.css";

/** How long a notice stays on screen before it clears itself. */
export const TOAST_DURATION_MS = 3200;

/**
 * Whether the notice reports something that worked or something that failed.
 * Only the leading glyph differs — a green tick on a "Couldn't save …" line
 * reads as success, which is the misreading this distinction exists to stop.
 */
export type ToastTone = "success" | "error";

export interface UseTransientToastReturn {
  /** The current message, or "" when nothing is showing. */
  message: string;
  /** Tone of the current message; "success" whenever nothing is showing. */
  tone: ToastTone;
  /** Show a message; it clears itself after {@link TOAST_DURATION_MS}. */
  show: (message: string, tone?: ToastTone) => void;
}

export const useTransientToast = (): UseTransientToastReturn => {
  const [{ message, tone }, setNotice] = useState<{ message: string; tone: ToastTone }>({
    message: "",
    tone: "success",
  });
  const show = useCallback((next: string, nextTone: ToastTone = "success") => {
    setNotice({ message: next, tone: nextTone });
  }, []);

  useEffect(() => {
    if (!message) return undefined;
    const id = setTimeout(() => setNotice({ message: "", tone: "success" }), TOAST_DURATION_MS);
    return () => clearTimeout(id);
  }, [message]);

  return { message, tone, show };
};

/** Renders nothing when there is no message, so callers can mount it unconditionally. */
export const VaultToast = ({
  message,
  tone = "success",
}: {
  message: string;
  tone?: ToastTone;
}) => {
  if (!message) return null;
  const isError = tone === "error";
  return (
    <div className={styles.toast} role={isError ? "alert" : "status"}>
      <span className={isError ? styles.toastAlert : styles.toastTick} aria-hidden>
        <Icon
          type={isError ? "exclamation-triangle" : "check"}
          size="small"
          customColor="#ffffff"
        />
      </span>
      {message}
    </div>
  );
};
